import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid"
import { useCallback, useState, useMemo } from "react"
import ModernMUITable from "../../../Components/ModernMUITable"
import { useModalProvider } from "../../../Providers/ModalProvider"
import InviteUserModal from "./Subcomponents/InviteUserModal"
import ModifyUserModal from "./Subcomponents/ModifyUserModal"
import EyeIcon from "../../../Components/Icons/EyeIcon"
import { MdOutlineModeEdit } from "react-icons/md"
import { useEndpointProvider } from "../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider"
import { useAuthProvider } from "../../../Providers/AuthProvider"
import { permissionTooltip } from "../../../Utils/permissionUtils"
import { useNavigate } from "react-router-dom"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { useEnvironmentVariablesProvider, Deployment } from "../../../Providers/EnvironmentVariablesProvider"

export default function UserManagement() {
	const { createModal, close } = useModalProvider()
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const queryClient = useQueryClient()
	const authProvider = useAuthProvider()
	const canManageUsers = authProvider.permissions?.['user:manage']
	const navigate = useNavigate()
	const { deployment } = useEnvironmentVariablesProvider()

	// Row selection state for delete functionality
	const [selectedUserIds, setSelectedUserIds] = useState([])

	const deleteDisabledReason = permissionTooltip(canManageUsers)
		|| (selectedUserIds.length === 0 ? "Select users to delete." : "")

	// Pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

	function inviteUserModal() {
		createModal(<InviteUserModal style={{ height: "80%" }} close={close} escClose={false} clickOutsideClose={false} />)
	}

	const handleClick = useCallback((row, viewMode) => {
		createModal(<ModifyUserModal style={{ height: "80%" }} close={close} row={row} escClose={false} clickOutsideClose={false} viewMode={viewMode} />)
	}, [createModal, close])

	// Delete users mutation
	const deleteMutation = useMutation({
		mutationFn: async (user_ids) => {
			return endpointProvider.post(LINKS.ADMIN.USER_MANAGEMENT.USERS.DELETE_USERS, { user_ids })
		},
		onSuccess: (data, user_ids) => {
			// Check if current user was deleted
			const currentUserId = authProvider.currentUser?.id
			const deletedSelf = user_ids.includes(currentUserId)
			
			if (deletedSelf) {
				// User deleted their own account - logout and redirect
				authProvider.logout()
				navigate("/")
			} else {
				// Normal deletion - just refresh the table
				setSelectedUserIds([])
				queryClient.invalidateQueries(["users"])
			}
		}
	})

	const handleDeleteUsers = () => {
		if (selectedUserIds.length > 0) {
			const currentUserId = authProvider.currentUser?.id
			const isDeletingSelf = selectedUserIds.includes(currentUserId)
			const userCount = selectedUserIds.length
			const isMultiple = userCount > 1
			
			let title, description, buttonText
			
			if (isDeletingSelf && isMultiple) {
				title = "Delete your account and other users?"
				description = `You are about to delete ${userCount} users, including your own account. You will be logged out immediately and lose access to the system. This action cannot be undone.`
				buttonText = "Yes, delete all users"
			} else if (isDeletingSelf && !isMultiple) {
				title = "Delete your own account?"
				description = "You are about to delete your own account. You will be logged out immediately and lose access to the system. This action cannot be undone."
				buttonText = "Yes, delete my account"
			} else {
				title = "Delete users?"
				description = `Are you sure you want to permanently delete ${userCount} user${isMultiple ? 's' : ''}? This action cannot be undone.`
				buttonText = "Yes, delete"
			}
			
			createModal(<ConfirmationModal
				title={title}
				description={description}
				confirmButton={{
					text: buttonText,
					theme: MobergTheme.RED,
					onClick: () => deleteMutation.mutate(selectedUserIds)
				}}
			/>)
		}
	}

	const handleSelectionChange = (selectionArray) => {
		setSelectedUserIds(selectionArray)
	}


	const isClinical = deployment === Deployment.CLINICAL
	
	const columns = useMemo(() => {
		const baseColumns = [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				hideable: false,
			},
			{ field: "email", flex: 1, headerName: "Email", minWidth: 220 },
			{ field: "first_name", flex: 0.5, headerName: "First name", minWidth: 100 },
			{ field: "last_name", flex: 0.5, headerName: "Last name", minWidth: 100 },
		];

		// Only include site and project count columns when not in clinical mode
		if (!isClinical) {
			baseColumns.push(
				{ field: "site_count", flex: 0.3, headerName: "No. Sites", valueGetter: (value, row) => row?.site_count, minWidth: 150 },
				{ field: "project_count", flex: 0.3, headerName: "No. projects", valueGetter: (value, row) => row?.project_count, minWidth: 120 }
			);
		}

		baseColumns.push(
			{ field: "role_count", flex: 0.3, headerName: "No. roles", valueGetter: (value, row) => row?.role_count, minWidth: 120 },
			{
				field: "is_verified",
				flex: 0.3,
				headerName: "Verified",
				renderCell: params => {
					return params.value ? <CheckCircleIcon color="success" /> : null
				},
				headerAlign: "center",
				align: "center",
				minWidth: 80,
				sortable: true
			},
			{
				field: "actions",
				flex: 0.4,
				headerName: "Actions",
				hideable: false,
				type: "actions",
				getActions: params => [
					<GridActionsCellItem icon={<EyeIcon />} label="View" onClick={() => handleClick(params.row, true)} showInMenu />,
					<GridActionsCellItem icon={<MdOutlineModeEdit size={20} />} label="Edit" onClick={() => handleClick(params.row)} showInMenu disabled={!canManageUsers} />,
				],
				minWidth: 120
			}
		);

		return baseColumns;
	}, [isClinical, handleClick, canManageUsers])

	const userTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: true },
		mainTable: true
	}

	const queryProps = {
		queryKey: "users",
		endpoint: LINKS.ADMIN.USER_MANAGEMENT.USERS.GET_USERS,
	}

	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

	return (
		<div style={{ 
			position: 'relative', 
			padding: '10px 30px 20px', 
			height: 'calc(100vh - 200px)',
			minHeight: '500px',
			maxHeight: '80vh'
		}}>
			{/* Action buttons above table */}
			<div style={{ paddingBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.BLUE}
					shape={MobergButtonShape.RECTANGLE}
					onClick={inviteUserModal}
					disabled={!canManageUsers}
					tooltip={permissionTooltip(canManageUsers)}
				>
					Invite user
				</MobergButton>
				
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.RED}
					shape={MobergButtonShape.RECTANGLE}
					onClick={handleDeleteUsers}
					disabled={!!deleteDisabledReason}
					tooltip={deleteDisabledReason}
				>
					Delete ({selectedUserIds.length})
				</MobergButton>
			</div>

			<ModernMUITable 
				columns={columns} 
				tableToolbarProps={userTableToolbarProps} 
				{...queryProps} 
				paginationMode="server"
				sortingMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			supportsPageSize={true}
				height="100%"
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={setColumnVisibilityModel}
				enableRowSelection={true}
				onSelectionChange={handleSelectionChange}
				isRowSelectable={() => canManageUsers}
				persistStateInUrl={true}
				tableId="users"
				defaultSortModel={[
					{ field: 'email', sort: 'asc' }
				]}
			/>
		</div>
	)
}
